import { formatHHMM } from "../lib/dateFormat";

interface DateLabelProps {
  date: string; // YYYY-MM-DD
  time?: string; // HH:MM
  className?: string;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function ordinal(n: number): string {
  if (n >= 11 && n <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

function formatDate(dateStr: string): string {
  const today = startOfDay(new Date());
  // Parse as local date (not UTC)
  const [year, month, day] = dateStr.split("-").map(Number);
  const target = new Date(year, month - 1, day);

  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";

  // This week (2-6 days from now)
  if (diffDays >= 2 && diffDays <= 6) {
    return dayNames[target.getDay()];
  }

  // Next week (7-13 days from now)
  if (diffDays >= 7 && diffDays <= 13) {
    return `Next ${dayNames[target.getDay()]}`;
  }

  // Past week (-6 to -2 days)
  if (diffDays >= -6 && diffDays <= -2) {
    return `Last ${dayNames[target.getDay()]}`;
  }

  // Same year
  const monthDay = `${monthNames[target.getMonth()]} ${ordinal(target.getDate())}`;
  if (target.getFullYear() === today.getFullYear()) {
    return monthDay;
  }

  // Different year
  return `${monthDay}, ${target.getFullYear()}`;
}

export function DateLabel({ date, time, className }: DateLabelProps) {
  const label = formatDate(date);
  const timeStr = time && time !== "00:00" ? formatHHMM(time) : "";
  const text =
    label === "Today" && timeStr ? timeStr
    : timeStr ? `${label} ${timeStr}`
    : label;
  return <span className={className}>{text}</span>;
}
